type BridgeCoursePackageEntry = {
  grade?: number | string;
  ExamId?: number | string;
  examId?: number | string;
  exam_id?: number | string;
  packageId?: number | string;
  packageid?: number | string;
  package_id?: number | string;
  id?: number | string;
};

type BridgeCourseProgramLike = {
  isDefault?: boolean;
  gradeId?: number;
  mdsGradeId?: number;
  examId?: number;
  mdsExamId?: number;
};

function parseMappingRaw(value: unknown): unknown {
  if (value == null || value === "") return null;

  if (typeof value === "string") {
    try {
      return parseMappingRaw(JSON.parse(value));
    } catch {
      return null;
    }
  }

  return value;
}

function packageIdFromEntry(entry: unknown): string | null {
  if (entry == null) return null;
  if (typeof entry === "number" && Number.isFinite(entry)) return String(entry);
  if (typeof entry === "string" && entry.trim()) return entry.trim();
  if (typeof entry === "object") {
    const rec = entry as BridgeCoursePackageEntry;
    const id =
      rec.packageId ?? rec.packageid ?? rec.package_id ?? rec.id;
    if (id == null) return null;
    const asString = String(id).trim();
    return asString ? asString : null;
  }
  return null;
}

function examIdFromEntry(entry: BridgeCoursePackageEntry): number | null {
  const raw = entry.ExamId ?? entry.examId ?? entry.exam_id;
  if (raw == null || raw === "") return null;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
}

function gradeFromEntry(entry: BridgeCoursePackageEntry): number | null {
  if (entry.grade == null || entry.grade === "") return null;
  const num = Number(entry.grade);
  return Number.isFinite(num) ? num : null;
}

function asLegacyGradeMap(
  value: unknown,
): Record<string, unknown> | null {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const rec = value as Record<string, unknown>;
  if (Array.isArray(rec.packages)) return null;
  return rec;
}

/** Default enrolled program used for bridge-course grade / exam lookup. */
export function getSelectedBridgeCourseProgram(
  enrolledPrograms: BridgeCourseProgramLike[] | null | undefined,
): BridgeCourseProgramLike | undefined {
  return enrolledPrograms?.find((p) => p.isDefault) ?? enrolledPrograms?.[0];
}

export function getBridgeCourseGradeFromProgram(
  program: BridgeCourseProgramLike | null | undefined,
): number | null {
  const grade = program?.mdsGradeId ?? program?.gradeId;
  return grade ?? null;
}

export function getBridgeCourseExamIdFromProgram(
  program: BridgeCourseProgramLike | null | undefined,
): number | null {
  const examId = program?.mdsExamId ?? program?.examId;
  return examId ?? null;
}

export type BridgeCourseReviewTarget = {
  packageId: string;
  grade: number;
  examId: number | null;
};

/** Resolve bridge-course review target from remote config and the selected program. */
export function resolveBridgeCourseReviewTarget(
  mappingRaw: unknown,
  enrolledPrograms: BridgeCourseProgramLike[] | null | undefined,
): BridgeCourseReviewTarget | null {
  const selectedProgram = getSelectedBridgeCourseProgram(enrolledPrograms);
  const grade = getBridgeCourseGradeFromProgram(selectedProgram);
  const examId = getBridgeCourseExamIdFromProgram(selectedProgram);

  console.log("[BridgeCourseDeeplink] resolveBridgeCourseReviewTarget", {
    enrolledProgramsCount: enrolledPrograms?.length ?? 0,
    selectedProgram: selectedProgram
      ? {
          isDefault: selectedProgram.isDefault,
          gradeId: selectedProgram.gradeId,
          mdsGradeId: selectedProgram.mdsGradeId,
          examId: selectedProgram.examId,
          mdsExamId: selectedProgram.mdsExamId,
        }
      : null,
    grade,
    examId,
    mappingRawType: mappingRaw == null ? "null" : typeof mappingRaw,
    mappingRaw:
      typeof mappingRaw === "string"
        ? mappingRaw.slice(0, 200)
        : mappingRaw,
  });

  if (grade == null) {
    console.warn(
      "[BridgeCourseDeeplink] abort: no grade from selected program",
    );
    return null;
  }

  const packageId = getPackageIdForGrade(mappingRaw, grade, examId);
  if (!packageId) {
    console.warn("[BridgeCourseDeeplink] abort: no packageId for grade/exam", {
      grade,
      examId,
    });
    return null;
  }

  console.log("[BridgeCourseDeeplink] resolved target", {
    packageId,
    grade,
    examId,
  });

  return { packageId, grade, examId };
}

/**
 * Resolve bridge-course packageId from remote config `bridge_course_grade_mapping`.
 * Supports legacy `{ "8": 2206 }` and `{ packages: [{ grade, ExamId, packageId }] }`.
 */
export function getPackageIdForGrade(
  mappingRaw: unknown,
  grade: number | string | null | undefined,
  examId?: number | string | null | undefined,
): string | null {
  if (grade == null || grade === "") return null;

  const parsed = parseMappingRaw(mappingRaw);
  if (parsed == null) return null;

  const gradeNum = Number(grade);
  if (!Number.isFinite(gradeNum)) return null;

  const examNum =
    examId != null && examId !== "" && Number.isFinite(Number(examId))
      ? Number(examId)
      : null;

  if (
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    Array.isArray((parsed as { packages?: unknown }).packages)
  ) {
    const packages = (parsed as { packages: BridgeCoursePackageEntry[] })
      .packages;

    for (const entry of packages) {
      const entryGrade = gradeFromEntry(entry);
      const entryExamId = examIdFromEntry(entry);
      if (entryGrade !== gradeNum) continue;
      if (examNum != null && entryExamId != null && entryExamId !== examNum) {
        continue;
      }
      if (examNum == null && entryExamId != null) continue;

      const packageId = packageIdFromEntry(entry);
      if (packageId) return packageId;
    }

    return null;
  }

  const legacyMap = asLegacyGradeMap(parsed);
  if (!legacyMap) return null;

  const gradeKey = String(grade);
  const entry = legacyMap[gradeKey] ?? legacyMap[String(gradeNum)];
  return packageIdFromEntry(entry);
}
